<template>
  <b-modal
    id="modal-batch-list"
    ref="modalBatchList"
    title="Select Batch"
    size="xl"
    hide-footer
    @shown="fetchBatches"
    @hidden="resetModal">
    <div>
      <!-- Course Info Header -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-2">{{ selectedCourse?.title }}</h5>
            <p class="text-muted mb-0">{{ selectedCourse?.description }}</p>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="batchLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading batches...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="batches.length === 0" class="text-center py-5">
        <i class="fe fe-inbox" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No batches available</p>
      </div>

      <!-- Batch Table -->
      <div v-else>
        <vue-good-table
          :columns="batchColumns"
          :rows="batches"
          :pagination-options="{ enabled: true }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'batch'">
              {{ formatBatchLabel(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'penyelenggara'">
              {{ formatPenyelenggara(props.row.penyelenggara) }}
            </span>
            <span v-else-if="props.column.field === 'batch_status'">
              <span class="badge" :class="getBatchStatusClass(props.row.batch_status)">
                {{ props.row.batch_status }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'action'">
              <div class="d-flex gap-2">
                <button
                  class="btn btn-sm btn-outline-info"
                  @click="viewSchedule(props.row, props.index)"
                  title="View Schedule">
                  <i class="fe fe-calendar"></i>
                </button>
                <button
                  class="btn btn-sm btn-outline-primary"
                  @click="viewAttendanceReport(props.row)"
                  title="View Attendance Report">
                  <i class="fe fe-eye"></i>
                </button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="downloadAttendanceReport(props.row)"
                  :disabled="downloadingReportId === props.row.training_event_id"
                  title="Download Attendance Report">
                  <i class="fe fe-download"></i>
                </button>
              </div>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <!-- Bulk Upload Modal -->
    <modal-bulk-upload
      :selectedCourseId="selectedCourse?.id"
      @upload-success="handleUploadSuccess"
    />
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import ModalBulkUpload from './modal-bulk-upload.vue';
import { formatBatchLabel, formatPenyelenggara } from '@/utils/batchIdentity';

export default {
  name: 'ModalBatchList',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    }
  },
  components: {
    VueGoodTable,
    ModalBulkUpload
  },
  computed: {
    ...mapGetters('mitraAttendance', ['batches', 'batchLoading'])
  },
  data() {
    return {
      isDownloading: false,
      downloadingReportId: null,
      batchColumns: [
        { label: 'Batch', field: 'batch' },
        { label: 'Penyelenggara', field: 'penyelenggara', width: '180px' },
        { label: 'Status', field: 'batch_status' },
        { label: 'Quota', field: 'quota' },
        { label: 'Enrolled', field: 'enrolled' },
        { label: 'Available', field: 'available_quota' },
        { label: 'Actions', field: 'action', width: '150px' }
      ]
    };
  },
  methods: {
    formatBatchLabel,
    formatPenyelenggara,
    async fetchBatches() {
      if (!this.selectedCourse?.id) return;

      try {
        await this.$store.dispatch('mitraAttendance/getBatches', this.selectedCourse.id);
      } catch (error) {
        console.error('Error fetching batches:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Daftar batch belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },

    resetModal() {
      // Reset any local state if needed
    },

    viewSchedule(batch, index) {
      this.$emit('view-schedule', { batch, index });
    },

    viewAttendanceReport(batch) {
      if (!batch?.training_event_id) return;
      this.$emit('view-attendance-report', batch.training_event_id);
    },

    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    async downloadTemplate() {
      if (this.isDownloading) return;

      if (!this.selectedCourse?.id) {
        this.$swal({
          title: 'Warning',
          text: 'Tidak ada pelatihan yang dipilih',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      this.isDownloading = true;
      try {
        const response = await this.$store.dispatch('mitraAttendance/downloadAttendanceTemplate', this.selectedCourse.id);

        // Create blob and download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Attendance_Template_${this.selectedCourse.title || 'Course'}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$swal({
          title: 'Success',
          text: 'Template berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal({
          title: 'Error',
          text: 'Template belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isDownloading = false;
      }
    },

    showBulkUploadModal() {
      if (!this.selectedCourse?.id) {
        this.$swal({
          title: 'Warning',
          text: 'Tidak ada pelatihan yang dipilih',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }
      this.$bvModal.show('modal-bulk-upload-attendance');
    },

    handleUploadSuccess(result) {
      console.log('✅ Upload success:', result);
      // Refresh batches after successful upload
      this.fetchBatches();
    },

    async downloadAttendanceReport(batch) {
      if (!batch?.training_event_id || this.downloadingReportId === batch.training_event_id) return;

      this.downloadingReportId = batch.training_event_id;
      try {
        const response = await this.$store.dispatch('attendanceReport/downloadReport', batch.training_event_id);
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `attendance-${this.selectedCourse?.title || 'report'}-batch-${batch.batch}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$swal({
          title: 'Success',
          text: 'Laporan absensi berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading attendance report:', error);
        this.$swal({
          title: 'Error',
          text: 'Laporan absensi belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.downloadingReportId = null;
      }
    }
  }
};
</script>

<style scoped>
.batch-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 2px solid #e9ecef;
}

.batch-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  border-color: #e63757;
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.me-1 {
  margin-right: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}
</style>
